import { useReducer, type MutableRefObject } from "react";
import { flushSync } from "react-dom";
import u2000FormState, { U2000FormStateEnumAction, U2000FormStateEnumEditMode, U2000FormStateEnumState, type U2000FormState } from "./U2000FormState";
import type { U2000GridStateEnumPaginationDirection } from "./U2000GridState";
import { useU2000FormLeaveGuardContext } from "../../context/formLeaveGuard/U2000FormLeaveGuardContext";
import { u2000IsFormRowDirty } from "../../tools/u2000FormDirtyUtils";

// Interface regroupant les handlers pour le form state
export interface U2000FormStateHandlers<T extends Record<string, any>> {
  [x: string]: any;
  setEditMode: (mode: U2000FormState["editMode"]) => void;
  setData: (row: T) => void;
  getData: () => T;
  saveData: () => void;
  getOldData: () => T | null;
  afterSaveData: (row: T) => void;
  /** BL-0711 — aligne current et old après GET serveur sans quitter l’édition (optionnel : handlers locaux de dialogue). */
  syncDataFromServer?: (row: T) => void;
  cancelData: () => void;
  getFormState: () => U2000FormState["state"];
  getFormEditMode: () => U2000FormState["editMode"];
  getTouched: () => U2000FormState["touched"];
  /** Marque un champ touché sans revalider (évite getData() périmé après setData synchrone). */
  markTouched: (fieldId: string) => void;
  getErrors: () => U2000FormState["errors"];
  setErrors: (errors: U2000FormState["errors"]) => void;
  setOtherErrors: (otherErrors: U2000FormState["otherErrors"]) => void;
  getOtherErrors: () => U2000FormState["otherErrors"];
  setSubmitted: (isSubmitted: boolean) => void;
  isSubmitted: () => boolean;
  isValid: () => boolean;
  getCustomAttributes: () => Record<string, any>;
  setCustomAttributes: (customAttributes: Record<string, any>) => void;

  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>, dataForValidation?: T) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, value: string | number | boolean | any | any[]) => void;

  onRecordChange: (record: T) => void;
  onPageChange: (page: number, direction: U2000GridStateEnumPaginationDirection) => void;
  onLeave: () => void;
  /** Navigation SPA après enregistrement / annulation (contourne useBlocker BL-0635). */
  runAfterFormCommitNavigation: (navigateFn: () => void) => void;
  getBusinessHandlers: () => U2000FormSBusinessHandlers<T>;
}

export interface U2000FormSBusinessHandlers<T extends Record<string, any>> {
  newRecord?: () => T;
  validateForm?: (data: T) => U2000FormState["errors"];
  saveForme?: (data: T, editMode: U2000FormStateEnumEditMode) => Promise<void>;
  cancelForm?: () => void;

  onChangeRecordForm?: (record: T) => void;
  onChangePageForm?: (page: number, direction: U2000GridStateEnumPaginationDirection) => void;
  onLeaveForm?: () => void;
  /** Si défini, remplace `blocker.proceed()` quand l'utilisateur confirme quitter avec saisie (ex. BL-0656 correction approbation). */
  confirmFormLeaveWithDirty?: () => void | Promise<void>;
}

export interface U2000FormStateHandlersParams<T extends Record<string, any>> {
  formState: U2000FormState;
  formStateDispatch: React.Dispatch<any>;
  businessHandlers: U2000FormSBusinessHandlers<T>;

  newRecord?: T;
  validateForm?: (data: T) => U2000FormState["errors"];
  saveForme?: (data: T, editMode: U2000FormStateEnumEditMode) => Promise<void>;
  cancelForm?: () => void;

  onChangeRecordForm?: (record: T) => void;
  onChangePageForm?: (page: number, direction: U2000GridStateEnumPaginationDirection) => void;
  onLeaveForm?: () => void;
}

export interface U2000FormStateHandlersReturn<T extends Record<string, any>> {
  setEditMode: (mode: U2000FormState["editMode"]) => void;
  setData: (row: T) => void;
  getData: () => T;
  saveData: () => void;
  getOldData: () => T | null;
  afterSaveData: (row: T) => void;
  /** BL-0711 — aligne current et old après GET serveur sans quitter l’édition. */
  syncDataFromServer: (row: T) => void;
  cancelData: () => void;
  getFormState: () => U2000FormState["state"];
  getFormEditMode: () => U2000FormState["editMode"];
  getTouched: () => U2000FormState["touched"];
  markTouched: (fieldId: string) => void;
  getErrors: () => U2000FormState["errors"];
  setErrors: (errors: U2000FormState["errors"]) => void;
  getOtherErrors: () => U2000FormState["otherErrors"];
  setOtherErrors: (otherErrors: U2000FormState["otherErrors"]) => void;
  setSubmitted: (isSubmitted: boolean) => void;
  isSubmitted: () => boolean;
  isValid: () => boolean;
  getCustomAttributes: () => Record<string, any>;
  setCustomAttributes: (customAttributes: Record<string, any>) => void;

  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>, dataForValidation?: T) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, value: string | number | boolean | any[]) => void;

  onRecordChange: (record: T) => void;
  onPageChange: (page: number, direction: U2000GridStateEnumPaginationDirection) => void;
  onLeave: () => void;
  runAfterFormCommitNavigation: (navigateFn: () => void) => void;

  getBusinessHandlers: () => U2000FormSBusinessHandlers<T>;
}

export const u2000IntFormState: U2000FormState = {
  current: null,
  state: U2000FormStateEnumState.READ,
  editMode: U2000FormStateEnumEditMode.NONE,
  old: null,
  touched: {} as U2000FormState["touched"], // objet vide au lieu de undefined
  errors: {} as U2000FormState["errors"], // objet vide au lieu de undefined
  otherErrors: {} as U2000FormState["otherErrors"], // objet vide au lieu de undefined
  isSubmitted: false,
  customAttributes: {},
};

/** Fenêtre courte où useBlocker ignore le garde-fou après commit formulaire (BL-0635). */
function u2000OpenFormCommitNavigationBypass(navigationBypassRef: MutableRefObject<boolean>) {
  navigationBypassRef.current = true;
  queueMicrotask(() => {
    navigationBypassRef.current = false;
  });
}

export function useU2000FormStateHandlers<T extends Record<string, any>>(params: Partial<U2000FormStateHandlersParams<T>> = {}): U2000FormStateHandlersReturn<T> {
  const [internalFormState, internalDispatch] = useReducer(u2000FormState, u2000IntFormState);
  const { navigationBypassRef, runNavigationWithoutFormLeaveBlock } = useU2000FormLeaveGuardContext();

  const formState = params?.formState ?? internalFormState;
  const formStateDispatch = params?.formStateDispatch ?? internalDispatch;
  const businessHandlers = params?.businessHandlers ?? {};

  const setEditMode = (mode: U2000FormStateEnumEditMode) => {
    formStateDispatch({ type: U2000FormStateEnumAction.SETEDITMODE, data: { mode } });
    if (mode === U2000FormStateEnumEditMode.ADD) {
      formStateDispatch({ type: U2000FormStateEnumAction.SETDATA, data: { row: params.businessHandlers?.newRecord ? params.businessHandlers.newRecord() : {} } });
    }
  };

  function getFormState(): U2000FormStateEnumState {
    return formState.state;
  }

  const getFormEditMode = (): U2000FormStateEnumEditMode => {
    return formState.editMode;
  };

  const setSubmitted = (isSubmitted: boolean) => {
    formStateDispatch({ type: U2000FormStateEnumAction.SETSUBMITTED, data: { isSubmitted } });
  };

  const saveData = () => {
    setSubmitted(true);
    let errors = getErrors();
    if (businessHandlers.validateForm) {
      errors = businessHandlers.validateForm(getData());
      formStateDispatch({ type: U2000FormStateEnumAction.SETERRORS, data: { errors } });
    }
    if (errors && Object.keys(errors).length > 0) {
      return;
    }
    if (businessHandlers.saveForme) {
      businessHandlers.saveForme(getData(), getFormEditMode());
    }
  };

  const getOldData = (): T | null => {
    return formState.old as T | null;
  };

  const afterSaveData = (row: T) => {
    flushSync(() => {
      formStateDispatch({ type: U2000FormStateEnumAction.AFTERSAVE, data: { row } });
    });
    u2000OpenFormCommitNavigationBypass(navigationBypassRef);
  };

  const syncDataFromServer = (row: T) => {
    flushSync(() => {
      formStateDispatch({ type: U2000FormStateEnumAction.SYNCFROMSERVER, data: { row } });
    });
  };

  const cancelData = () => {
    flushSync(() => {
      formStateDispatch({ type: U2000FormStateEnumAction.CANCEL });
    });
    u2000OpenFormCommitNavigationBypass(navigationBypassRef);
    businessHandlers.cancelForm && businessHandlers.cancelForm();
  };

  const runAfterFormCommitNavigation = (navigateFn: () => void) => {
    runNavigationWithoutFormLeaveBlock(navigateFn);
  };

  const isSubmitted = (): boolean => {
    return formState.isSubmitted;
  };

  const isValid = (): boolean => {
    return !formState.errors || Object.keys(formState.errors).length === 0;
  };

  const getData = () => {
    return formState.current as T;
  };

  const setData = (row: T) => {
    formStateDispatch({ type: U2000FormStateEnumAction.SETDATA, data: { row } });
    // BL-0394 : setData doit rafraîchir errors (comme onChange). Sinon un autocomplete
    // qui commit via setData laisse isValid() bloqué sur d’anciennes erreurs (ex. state.id
    // requis après clear pays, puis province resélectionnée → Enregistrer reste disabled).
    if (businessHandlers.validateForm) {
      formStateDispatch({
        type: U2000FormStateEnumAction.SETERRORS,
        data: { errors: businessHandlers.validateForm(row) },
      });
    }
  };

  const getTouched = () => {
    return formState.touched;
  };

  const markTouched = (fieldId: string) => {
    formStateDispatch({ type: U2000FormStateEnumAction.SETTOUCHED, data: { id: fieldId } });
  };

  const getErrors = () => {
    return formState.errors;
  };

  const setErrors = (errors: U2000FormState["errors"]) => {
    formStateDispatch({ type: U2000FormStateEnumAction.SETERRORS, data: { errors } });
  };

  const getOtherErrors = () => {
    return formState.otherErrors;
  };

  const setOtherErrors = (otherErrors: U2000FormState["otherErrors"]) => {
    formStateDispatch({ type: U2000FormStateEnumAction.SETOTHERERRORS, data: { otherErrors } });
  };

  const getCustomAttributes = () => {
    return formState.customAttributes;
  };

  const setCustomAttributes = (customAttributes: Record<string, any>) => {
    formStateDispatch({ type: U2000FormStateEnumAction.SETCUSTOMATTRIBUTES, data: { customAttributes } });
  };

  const onFocus = (_e: React.FocusEvent<HTMLInputElement>) => {
    // Placeholder for focus logic if needed
  };

  /**
   * @param dataForValidation Snapshot à valider (ex. juste écrit via setData).
   * Sans override, getData() peut encore être périmé dans le même tick que setData.
   */
  const onBlur = (e: React.FocusEvent<HTMLInputElement>, dataForValidation?: T) => {
    formStateDispatch({ type: U2000FormStateEnumAction.SETTOUCHED, data: { id: e.target.id } });
    if (businessHandlers.validateForm) {
      const data = dataForValidation !== undefined ? dataForValidation : getData();
      formStateDispatch({ type: U2000FormStateEnumAction.SETERRORS, data: { errors: businessHandlers.validateForm(data) } });
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>, value: string | number | boolean | any[]) => {
    let currentValue: {};
    if (String(e.target.id).includes(".")) {
      currentValue = setByPath(getData() as Record<string, any>, e.target.id, value);
    } else {
      currentValue = { ...(getData() as Record<string, any>), [e.target.id]: value };
    }

    formStateDispatch({ type: U2000FormStateEnumAction.SETVALUE, data: { id: e.target.id, value } });
    businessHandlers.validateForm && formStateDispatch({ type: U2000FormStateEnumAction.SETERRORS, data: { errors: businessHandlers.validateForm(currentValue as T) } });
  };

  const onRecordChange = (record: T) => {
    businessHandlers.onChangeRecordForm && businessHandlers.onChangeRecordForm(record);
  };

  const onPageChange = (page: number, direction: U2000GridStateEnumPaginationDirection) => {
    businessHandlers.onChangePageForm && businessHandlers.onChangePageForm(page, direction);
  };

  const onLeave = () => {
    if (!businessHandlers.onLeaveForm) {
      return;
    }
    const inEdit = getFormState() === U2000FormStateEnumState.EDIT;
    const dirty = u2000IsFormRowDirty(getData(), getOldData());
    if (inEdit && dirty) {
      businessHandlers.onLeaveForm();
      return;
    }
    runNavigationWithoutFormLeaveBlock(() => {
      businessHandlers.onLeaveForm!();
    });
  };

  const getBusinessHandlers = () => {
    return businessHandlers;
  };

  // Deep clone utility to avoid mutation issues
  function deepClone<T>(obj: T): T {
    return obj === undefined ? obj : JSON.parse(JSON.stringify(obj));
  }
  // helper: set value by dotted path (returns new object)
  function setByPath(obj: any, path: string | number, value: any) {
    const cloned = deepClone(obj ?? {});
    const parts = String(path).split(".");
    let cur = cloned;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      if (cur[p] === undefined || cur[p] === null || typeof cur[p] !== "object") {
        cur[p] = {};
      }
      cur = cur[p];
    }
    cur[parts[parts.length - 1]] = value;
    return cloned;
  }

  return {
    setEditMode,
    setData,
    getData,
    getOldData,
    getFormState,
    getFormEditMode,
    getTouched,
    markTouched,
    getErrors,
    setErrors,
    getOtherErrors,
    setOtherErrors,
    isSubmitted,
    onFocus,
    onBlur,
    onChange,
    saveData,
    afterSaveData,
    syncDataFromServer,
    cancelData,
    setSubmitted,
    isValid,
    getCustomAttributes,
    setCustomAttributes,
    onRecordChange,
    onPageChange,
    onLeave,
    runAfterFormCommitNavigation,
    getBusinessHandlers,
  };
}
